import React from 'react';
import { addMonths, differenceInMonths } from 'date-fns';
import WhatsNextPage from './modules/WhatsNextPage';
import CurrentTenantEndDate from './modules/CurrentTenantEndDate';
import CurrentTenantEndDateShortened from './modules/CurrentTenantEndDateShortened';
import CurrentTenantM2MShortened from './modules/CurrentTenantM2MShortened';
import NewTenantInfo from './modules/NewTenantInfo';
import NewTenantDueDate from './modules/NewTenantDueDate';
import NewTenantTermLength from './modules/NewTenantTermLength';
import NewTenantRentAmount from './modules/NewTenantRentAmount';
import NewTenantConfirmation from './modules/NewTenantConfirmation';
import MoveInCostsPage from '@rental-manager/pages/Onboarding/MoveInCostsPage';
import KnowledgeBasedAuthenticationPage from '@rental-manager/pages/Onboarding/KnowledgeBasedAuthenticationPage';
import Modal from '@common/components/Modal';
import userPersonas from '@common/constants/userPersonas';
import { pageFlows } from './pageFlows';
import { getFirstPossibleDueDate } from '@common/paymentsUtils/dateUtils';
import type { MoveInCostsSettingsInterface, NewTermSettings } from '@rental-manager/pages/Dashboard/types';
import type { EndTermOptions } from '@common/types';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';

type AdditionalLeaseSelectorProps = {
  index: number | null;
};
type LeaseSelectorProps = ILeasesForPropertyModel & AdditionalLeaseSelectorProps;

type Props = {
  today: Date;
  propertyId: string;
  baseUrl: string;
  fromDashboard: boolean;
  landlordStatus: string;
  listing: { [key: string]: any };
  firstDueDate: string;
  currentTermEndDate: string;
  firstAvailableEndDate: string;
  listingTitle: string;
  requestUpdateCurrentTerm: (...args: any) => Record<string, any>;
  moveInCostsSettings: MoveInCostsSettingsInterface;
  paymentDayOfTheMonth: number;
  monthToMonth: boolean;
  rentTermsKey: string;
  requestCreateNewTerm: (...args: any) => void;
  onClose: () => void;
  endTermOptions: EndTermOptions;
  tenantFirstName: string;
  landlordEmail: string;
  currentTenantEmail: string;
  isRentGuaranteeEnabled: boolean;
  leasesForProperty: Array<LeaseSelectorProps>;
  newTermSettings: NewTermSettings;
};

type State = {
  pageHistory: any;
  newTermSettings: NewTermSettings;
  editMode?: boolean;
  firstPossibleDueDate?: Date;
};

class SetupNewTermModal extends React.Component<Props, State> {
  static defaultProps = {
    today: new Date(),
    paymentDayOfTheMonth: 1,
    propertyId: '',
    baseUrl: '',
    fromDashboard: false,
    landlordStatus: '',
    newTermSettings: {
      tenantFirstName: '',
      tenantLastName: '',
      tenantEmail: '',
      firstDueDate: '',
      numberOfPayments: '',
      monthToMonth: false,
      rentAmount: 0,
      leaseId: null,
      isSelectedLeaseRProEligible: false,
      currentPage: pageFlows.HowItWorks,
      paymentDayOfTheMonth: 1,
      firstAvailableEndDate: '',
    },
  };

  constructor(props: Props) {
    super(props);
    let page = props.newTermSettings.currentPage;
    if (props.newTermSettings.currentPage === pageFlows.CurrentTenantEndDate) {
      if (!props.firstAvailableEndDate) {
        page = pageFlows.NewTenantInfo;
      } else if (props.endTermOptions.mustShortenTerm) {
        page = pageFlows.CurrentTenantEndDateShortened;
      } else if (props.monthToMonth) {
        page = pageFlows.CurrentTenantM2MShortened;
      } else {
        page = pageFlows.CurrentTenantEndDate;
      }
    }
    this.state = {
      pageHistory: [page],
      newTermSettings: {
        ...props.newTermSettings,
        firstAvailableEndDate: this.props.firstAvailableEndDate,
      },
    };
  }

  goToChooseEndDate = (): void => {
    const { endTermOptions, monthToMonth, firstAvailableEndDate } = this.props;

    if (!firstAvailableEndDate) {
      this.setCurrentPage(pageFlows.NewTenantInfo);
    } else if (endTermOptions.mustShortenTerm) {
      this.setCurrentPage(pageFlows.CurrentTenantEndDateShortened);
    } else if (monthToMonth) {
      this.setCurrentPage(pageFlows.CurrentTenantM2MShortened);
    } else {
      this.setCurrentPage(pageFlows.CurrentTenantEndDate);
    }
  };

  setCurrentPage = (currentPage: number, moreState = {}): void => {
    const pageHistory = Object.assign([], this.state.pageHistory);
    pageHistory.push(currentPage);
    this.setState({ pageHistory, ...moreState });
  };

  updateCurrentTermEndDate = async (payload: any): Promise<void> => {
    const { finalPaymentDue } = payload;
    const { rentTermsKey, firstDueDate } = this.props;
    const totalNumberOfPayments = Math.abs(differenceInMonths(finalPaymentDue, firstDueDate)) + 1;
    const { editMode, newTermSettings } = this.state;
    const updatedNewTermSettings = { ...newTermSettings, ...payload };
    const response = await this.props.requestUpdateCurrentTerm({
      numberOfPayments: totalNumberOfPayments,
      rentTermsKey,
      firstPossibleDueDate: getFirstPossibleDueDate(updatedNewTermSettings),
    });

    if (!response.error) {
      this.setCurrentPage(pageFlows.NewTenantInfo, {
        editMode,
        newTermSettings: updatedNewTermSettings,
        firstPossibleDueDate: getFirstPossibleDueDate(updatedNewTermSettings),
      });
    }
  };

  updateCurrentTermEndDateFromMonthToMonth = async ({ finalPaymentDue, ...payload }: any): Promise<void> => {
    const { rentTermsKey, firstDueDate } = this.props;
    const { editMode, newTermSettings } = this.state;
    const totalNumberOfPayments = Math.abs(differenceInMonths(finalPaymentDue, firstDueDate)) + 1;

    const response = await this.props.requestUpdateCurrentTerm({
      numberOfPayments: totalNumberOfPayments,
      rentTermsKey,
      firstPossibleDueDate: addMonths(finalPaymentDue, 1),
    });

    if (!response.error) {
      this.setCurrentPage(pageFlows.NewTenantInfo, {
        editMode,
        newTermSettings: Object.assign({}, newTermSettings, payload),
        firstPossibleDueDate: addMonths(finalPaymentDue, 1),
      });
    }
  };

  goToPrevPage = (): void => {
    const { editMode, newTermSettings } = this.state;
    const pageHistory = Object.assign([], this.state.pageHistory);

    if (pageHistory[pageHistory.length - 1] > 0) {
      let newPage = pageHistory.pop();

      if (editMode && newPage !== pageFlows.NewTenantConfirmation) {
        newPage = pageFlows.NewTenantConfirmation;
      }

      this.setCurrentPage(newPage, {
        pageHistory,
        newTermSettings: Object.assign({}, newTermSettings),
      });
    }
  };

  goToSpecificPageFlow = (pageFlow: number): void => {
    const { editMode } = this.state;
    const pageHistory = Object.assign([], this.state.pageHistory);

    const newPage = pageFlow;
    pageHistory.push(newPage);

    this.setCurrentPage(newPage, {
      pageHistory,
      editMode: editMode === true || newPage === pageFlows.NewTenantConfirmation,
    });
  };

  goToConfirmation = (): void => this.goToSpecificPageFlow(pageFlows.NewTenantConfirmation);

  goToExperianFlow = (): void => this.goToSpecificPageFlow(pageFlows.NewTenantExperian);

  goToNextPage = (payload = {}): void => {
    const { editMode, newTermSettings } = this.state;
    const pageHistory = Object.assign([], this.state.pageHistory);

    let newPage = pageHistory[pageHistory.length - 1] + 1;
    if (editMode && newPage !== pageFlows.NewTenantTermLength) {
      newPage = pageFlows.NewTenantConfirmation;
    }

    pageHistory.push(newPage);

    this.setCurrentPage(newPage, {
      pageHistory,
      editMode: editMode === true || newPage === pageFlows.NewTenantConfirmation,
      newTermSettings: Object.assign({}, newTermSettings, payload),
    });
  };

  requestCreateNewTerm = async (): Promise<void> => {
    await this.props.requestCreateNewTerm(this.state.newTermSettings);
  };

  onClose = (): void => {
    this.props.onClose();
  };

  renderCurrentPage = (): React.ReactNode => {
    const { pageHistory, newTermSettings } = this.state;
    const currentPage = pageHistory[pageHistory.length - 1];

    if (currentPage === pageFlows.HowItWorks) {
      return <WhatsNextPage onSubmit={this.goToChooseEndDate} />;
    } else if (currentPage === pageFlows.CurrentTenantEndDate) {
      return (
        <CurrentTenantEndDate
          endTermOptions={this.props.endTermOptions}
          tenantFirstName={this.props.tenantFirstName}
          onSubmit={this.updateCurrentTermEndDate}
        />
      );
    } else if (currentPage === pageFlows.CurrentTenantEndDateShortened) {
      return (
        <CurrentTenantEndDateShortened
          endTermOptions={this.props.endTermOptions}
          currentTermEndDate={this.props.currentTermEndDate}
          tenantFirstName={this.props.tenantFirstName}
          onSubmit={this.updateCurrentTermEndDate}
        />
      );
    } else if (currentPage === pageFlows.CurrentTenantM2MShortened) {
      return (
        <CurrentTenantM2MShortened
          firstAvailableEndDate={this.props.firstAvailableEndDate}
          tenantFirstName={this.props.tenantFirstName}
          onSubmit={this.updateCurrentTermEndDateFromMonthToMonth}
        />
      );
    } else if (currentPage === pageFlows.NewTenantInfo) {
      return (
        <NewTenantInfo
          tenantFirstName={newTermSettings.tenantFirstName}
          tenantLastName={newTermSettings.tenantLastName}
          tenantEmail={newTermSettings.tenantEmail}
          landlordEmail={this.props.landlordEmail}
          currentTenantEmail={this.props.currentTenantEmail}
          onSubmit={this.goToNextPage}
        />
      );
    } else if (currentPage === pageFlows.NewTenantDueDate) {
      return (
        <NewTenantDueDate
          paymentDayOfTheMonth={newTermSettings.paymentDayOfTheMonth}
          onSubmit={this.goToNextPage}
          onCancel={this.goToPrevPage}
        />
      );
    } else if (currentPage === pageFlows.NewTenantTermLength) {
      return (
        <NewTenantTermLength
          paymentDayOfTheMonth={newTermSettings.paymentDayOfTheMonth}
          firstDueDate={this.state.firstPossibleDueDate}
          onSubmit={this.goToNextPage}
          onCancel={this.goToPrevPage}
        />
      );
    } else if (currentPage === pageFlows.NewTenantRentAmount) {
      return (
        <NewTenantRentAmount
          rentAmount={newTermSettings.rentAmount}
          onSubmit={this.goToNextPage}
          onCancel={this.goToPrevPage}
        />
      );
    } else if (currentPage === pageFlows.NewTenantMoveInCostsPage) {
      return (
        <MoveInCostsPage
          propertyId={this.props.propertyId}
          baseUrl={this.props.baseUrl}
          landlordStatus={this.props.landlordStatus}
          listing={this.props.listing}
          fromDashboard={this.props.fromDashboard}
          setupNewTerm
          goToExperianFlow={this.goToExperianFlow}
          goToConfirmation={this.goToConfirmation}
        />
      );
    } else if (currentPage === pageFlows.NewTenantExperian) {
      return (
        <KnowledgeBasedAuthenticationPage
          propertyId={this.props.propertyId}
          baseUrl={this.props.baseUrl}
          landlordStatus={this.props.landlordStatus}
          fromDashboard={this.props.fromDashboard}
          goToPrevPage={this.goToPrevPage}
          setupNewTerm
          goToConfirmation={this.goToConfirmation}
        />
      );
    } else if (currentPage === pageFlows.NewTenantConfirmation) {
      return (
        <NewTenantConfirmation
          {...newTermSettings}
          listingTitle={this.props.listingTitle}
          goToPage={this.setCurrentPage}
          onSubmit={this.requestCreateNewTerm}
          moveInCostsSettings={this.props.moveInCostsSettings}
        />
      );
    }
    return <></>;
  };

  render(): React.ReactNode {
    const currentPage = this.state.pageHistory[this.state.pageHistory.length - 1];

    const hasHistory =
      currentPage !== pageFlows.HowItWorks &&
      currentPage !== pageFlows.NewTenantConfirmation &&
      this.state.pageHistory.length > 1;

    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header="SET UP NEW TERM"
        dataAutoTestId="landlord-setup-new-term"
        onClickBack={!hasHistory ? null : this.goToPrevPage}
        onRequestClose={this.onClose}
      >
        {this.renderCurrentPage()}
      </Modal>
    );
  }
}

export default SetupNewTermModal;
